import { TooltipPosition } from './../../../../../libs/angular-shared/src/lib/directives/tooltip/tooltip-position.enum';
import { AppwriteDatabaseService } from '@prism/angular-shared';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { DialogReturnType, DialogService, SnackbarService, SnackbarStatus } from '@prism/angular-shared';
import { ClientDevice, ClientDeviceInformationDialogArgs, ClientDeviceType, EditSoftwareRequestDialogArgs, EditSoftwareRequestDialogReturnValue, HumanResourcesSubModule, isDefinedAssertType, listenForResult, MetalsSubModule, PrismCommonSubModule, PrismModule, toDisplayName, User, UserNotification, VacationEvent, Employee, vacationEmployeeDisplay } from '@prism/prism-shared';
import { BehaviorSubject, catchError, combineLatest, filter, first, from, map, merge, Observable, of, startWith, Subject, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';
import { ClientDeviceInformationDialogComponent } from '../shared/components/client-device-information-dialog/client-device-information-dialog.component';
import { ScreenService } from '../shared/services/screen.service';
import { UserApiService } from '../shared/services/user-api.service';
import { CoreActions, CoreSelectors } from '../shared/state/core';
import { EditSoftwareRequestDialogComponent } from '../shared/components/edit-software-request-dialog/edit-software-request-dialog.component';
import { PrismCommonActions, PrismCommonSelectors } from '../shared/state/factory/common';
import { AppwriteAuthService } from '../shared/services/appwrite-auth.service';
import { PrismCommonApiService } from '../shared/services/prism-common-api.service';

interface HeaderLink {
  label: string;
  routerLink: PrismModule;
}

interface Breadcrumb {
  label: string;
  url: string;
}

interface DisplayVacationEvent extends VacationEvent {
  employeeName: string;
  employeeTitle: string;
  employeeImageUrl: string;
  employeeInitials: string;
  dateLabel: string;
  activeToday: boolean;
}

@Component({
  selector: 'prism-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {

  @ViewChild('notificationPanel') notificationPanel!: ElementRef
  @ViewChild('bellIcon') bellIcon!: ElementRef
  @ViewChild('vacationPanel') vacationPanel!: ElementRef
  @ViewChild('vacationIcon') vacationIcon!: ElementRef

  TooltipPosition = TooltipPosition;
  ClientDeviceType = ClientDeviceType; // enum
  PrismModule = PrismModule; // enum

  public headerLinks$!: Observable<HeaderLink[]>;
  public breadcrumbs$!: Observable<Breadcrumb[]>;
  public currentUser$!: Observable<User>;
  public enableBreadcrumbs$!: Observable<boolean>;
  public userCanRequestSoftware$!: Observable<boolean>;
  public userCanDirectMessage$!: Observable<boolean>;
  public currentClientDevice$!: Observable<ClientDevice | null>;
  public mobileUser: boolean = false

  public mobileMenuExpanded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private mobileScreenSize: number = 1200
  public moduleOptions: PrismModule[] = [
    PrismModule.PrismCommon,
    PrismModule.Plastics,
    PrismModule.Metals,
    PrismModule.InjectionMolding,
    PrismModule.Automation,
    PrismModule.Admin,
    PrismModule.Settings
  ]
  private unsubscribe$: Subject<void> = new Subject<void>();

  public userNotifications$!: Observable<UserNotification[]>
  public vacationEvents$!: Observable<DisplayVacationEvent[]>
  public vacationCount$!: Observable<number>
  public userCanManageVacations$!: Observable<boolean>

  public hardcodedNotifications$: BehaviorSubject<UserNotification[]> = new BehaviorSubject<UserNotification[]>([])

  public unacknowledgedNotificationCount$!: Observable<number>
  private latestUserNotifications: UserNotification[] = [];
  private userNotificationsSubject$: BehaviorSubject<UserNotification[]> = new BehaviorSubject<UserNotification[]>([]);
  private unacknowledgedNotificationCountSubject$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private optimisticAcknowledgementById: Map<string, Date | null> = new Map<string, Date | null>();
  private vacationEventsSubject$: BehaviorSubject<VacationEvent[]> = new BehaviorSubject<VacationEvent[]>([]);
  private vacationCountSubject$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  public showNotifications$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  public showVacations$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public notificationContainerHeight: string = 'calc(100vh - 4rem)';
  public notificationContainerTop: string = '4rem';
  @ViewChild('headerBar') headerBar!: ElementRef;
  @ViewChild('mobileHeaderBar') mobileHeaderBar!: ElementRef;


  constructor(
    public screen: ScreenService,
    public router: Router,
    public store: Store,
    private userApiService: UserApiService,
    private dialog: DialogService<EditSoftwareRequestDialogReturnValue>,
    private snackbarService: SnackbarService,
    private appwrite: AppwriteDatabaseService,
    private appwriteAuth: AppwriteAuthService,
    private commonApi: PrismCommonApiService,
  ) { }

  @HostListener('window:focus')
  onFocus(): void {
    this.mobileUser = window.outerWidth < this.mobileScreenSize || window.innerHeight > window.innerWidth
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (!this.showNotifications$.value && !this.showVacations$.value) return;

    if (!this.mobileUser && !this.headerBar?.nativeElement) return;

    if (this.mobileUser && !this.mobileHeaderBar?.nativeElement) return;

    const headerRect = this.mobileUser ? this.mobileHeaderBar.nativeElement.getBoundingClientRect() : this.headerBar.nativeElement.getBoundingClientRect();
    const headerInView = headerRect.bottom > 0;

    if (headerInView) {
      const topOffset = headerRect.bottom;
      this.notificationContainerTop = `${topOffset}px`;
      this.notificationContainerHeight = `calc(100vh - ${topOffset}px)`;
    } else {
      this.notificationContainerTop = '0';
      this.notificationContainerHeight = '100vh';
    }
  }

  ngOnInit(): void {
    // Set the responsive branch before Angular's first view check. Changing it
    // in AfterViewInit caused NG0100 errors on tablet/phone routes.
    this.mobileUser = window.outerWidth < this.mobileScreenSize || window.innerHeight > window.innerWidth;
    this.currentUser$ = this.store.select(CoreSelectors.getCurrentUser).pipe(
      filter((user) => !!user)
    );

    this.breadcrumbs$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith({ urlAfterRedirects: this.router.url } as NavigationEnd),
      map(event => {
        const url = event.urlAfterRedirects.split('?')[0];
        const segments = url.split('/').filter(s => s.length > 0);
        const crumbs: Breadcrumb[] = [];
        let path = '';
        const depth = Math.min(segments.length, 3);
        if (segments.length < 2) return [];
        for (let i = 0; i < depth; i++) {
          path += '/' + segments[i];
          crumbs.push({ label: toDisplayName(segments[i]), url: path });
        }
        return crumbs;
      }),
    );

    this.userCanRequestSoftware$ = this.currentUser$.pipe(
      map(u => u.abilities[PrismModule.PrismCommon][PrismCommonSubModule.SoftwareRequests].visible)
    )
    this.userCanDirectMessage$ = this.currentUser$.pipe(
      map(u => u.abilities[PrismModule.PrismCommon][PrismCommonSubModule.DirectMessages].visible)
    )

    this.headerLinks$ = this.currentUser$.pipe(
      filter(user => !!user),
      map((user) => {
        const headerLinks: HeaderLink[] = [];
        if (user.abilities[PrismModule.PrismCommon].visible) {
          headerLinks.push({ label: 'common', routerLink: PrismModule.PrismCommon })
        }
        if (user.abilities[PrismModule.Plastics].visible) {
          headerLinks.push({ label: 'plastics', routerLink: PrismModule.Plastics })
        }
        if (user.abilities[PrismModule.C3].visible) {
          headerLinks.push({ label: 'c3', routerLink: PrismModule.C3 })
        }
        if (user.abilities[PrismModule.Metals].visible) {
          headerLinks.push({ label: 'metals', routerLink: PrismModule.Metals })
        }
        if (user.abilities[PrismModule.InjectionMolding]?.visible) {
          headerLinks.push({ label: 'injection molding', routerLink: PrismModule.InjectionMolding })
        }
        if (user.abilities[PrismModule.Printing3d]?.visible && user.abilities[PrismModule.Printing3d]?.products?.visible) {
          headerLinks.push({ label: '3D printing', routerLink: PrismModule.Printing3d })
        }
        if (user.abilities[PrismModule.Automation].visible) {
          headerLinks.push({ label: 'automation', routerLink: PrismModule.Automation })
        }
        if (user.abilities[PrismModule.Admin].visible) {
          headerLinks.push({ label: 'admin', routerLink: PrismModule.Admin })
        }
        return headerLinks;
      })
    )

    this.currentClientDevice$ = this.store.select(CoreSelectors.getCurrentClientDevice);

    this.userNotifications$ = merge(
      this.userNotificationsSubject$.asObservable(),
      this.currentUser$.pipe(
        filter(user => !!user),
        switchMap(user => this.userApiService.getUserNotificationsValueChanges(user.id)),
        // Server/poll path: apply optimistic overlays and clear only once the server agrees.
        map(userNotifications => this.toDisplayedUserNotifications(userNotifications, true)),
        tap(userNotifications => {
          this.latestUserNotifications = userNotifications;
          // Count from the optimistic-aware list so a stale poll can't bounce the badge
          // while acknowledges are still in flight.
          this.setUnacknowledgedNotificationCount(
            userNotifications.filter(n => !n.acknowledgedOn).length
          );
        }),
      ),
    );
    this.unacknowledgedNotificationCount$ = this.unacknowledgedNotificationCountSubject$.asObservable();

    this.currentUser$.pipe(
      filter(user => !!user),
      switchMap(user => this.userApiService.getUnacknowledgedUserNotificationCountValueChanges(user.id)),
      tap(count => {
        // While optimistic acks are pending, the list path owns the badge count.
        if (this.optimisticAcknowledgementById.size > 0) return;
        this.setUnacknowledgedNotificationCount(count);
      }),
      takeUntil(this.unsubscribe$),
    ).subscribe();

    this.userCanManageVacations$ = this.currentUser$.pipe(
      map(user => !!user.abilities[PrismModule.PrismCommon]?.[PrismCommonSubModule.HumanResources]?.CanEdit)
    );

    this.vacationEvents$ = this.vacationEventsSubject$.pipe(
      switchMap(events => combineLatest([
        of(events),
        this.store.select(CoreSelectors.getUsersDict),
        this.loadVacationEmployees(events),
      ])),
      map(([events, userDict, employeeDict]) => this.toDisplayVacationEvents(events, userDict, employeeDict)),
    );
    this.vacationCount$ = this.vacationCountSubject$.asObservable();
    this.loadVacationCount();

  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  @HostListener('window:resize')
  onResize() {
    this.mobileUser = window.outerWidth < this.mobileScreenSize || window.innerHeight > window.innerWidth
  }

  public allNotificationsAcknowledged(notifications: UserNotification[]) {
    return notifications.every(n => !!n.acknowledgedOn)
  }

  public logout() {
    from(this.appwriteAuth.signOut()).pipe(
      first(),
      tap(() => {
        this.store.dispatch(CoreActions.ClearCurrentUser());
        this.router.navigate(['/login']);
      })
    ).subscribe()
  }

  public settings() {
    this.router.navigate([PrismModule.Settings])
  }

  public navigateToDirectMessages() {
    this.router.navigate([PrismModule.PrismCommon, PrismCommonSubModule.DirectMessages])
  }

  public navigateToVacations() {
    this.router.navigate([PrismModule.PrismCommon, PrismCommonSubModule.HumanResources, HumanResourcesSubModule.Vacation])
    this.showVacations$.next(false)
  }

  public launchClientDeviceInformationDialog() {

    this.store.select(CoreSelectors.getCurrentClientDevice).pipe(
      first(),
      tap(clientDevice => {

        if (!clientDevice) {
          return;
        }

        const title = clientDevice.name;

        const args: ClientDeviceInformationDialogArgs = {
          clientDevice
        }


        {
          this.dialog.openDialog(ClientDeviceInformationDialogComponent, title, { args })
        }
      })
    ).subscribe();


  }

  public navigateToModule(event: MouseEvent, module: PrismModule) {
    event.stopPropagation()
    this.router.navigate([module])
    this.mobileMenuExpanded$.next(false)
  }

  public toggleMenuDropdown() {
    this.mobileMenuExpanded$.next(true)
  }

  public navigateToScanner() {
    this.router.navigate([PrismModule.Metals, MetalsSubModule.ProcessStation, 'tablet'])
    this.mobileMenuExpanded$.next(false)
  }

  @HostListener('document:click', ['$event'])
  onButtonClick(event: MouseEvent) {
    const element = event.target as HTMLElement
    if (element.id !== "mobile-menu-panel" && element.id !== "menu-toggle") {
      this.mobileMenuExpanded$.next(false)
    }
  }


  public refreshPage() {
    window.location.reload();
  }

  public openNotificationUrl(url: string) {
    if (url.length === 0) return;
    if (this.mobileUser) {
      this.router.navigateByUrl(url)
    } else {
      window.open(url, '_blank')
    }

  }

  public toggleNotificationAcknowledgement(notification: UserNotification, event: MouseEvent) {
    event.stopPropagation()
    const now = notification.acknowledgedOn ? null : new Date();
    const userNotification: UserNotification = {
      id: notification.id,
      description: notification.description,
      userId: notification.userId,
      associatedUrl: notification.associatedUrl,
      createdOn: notification.createdOn,
      acknowledgedOn: now,
    }
    this.optimisticAcknowledgementById.set(userNotification.id, now);
    this.updateDisplayedUserNotifications([userNotification]);

    this.userApiService.setUserNotification(userNotification).pipe(
      first(),
      tap(() => this.userApiService.refreshUserNotifications()),
      catchError(() => {
        this.optimisticAcknowledgementById.delete(userNotification.id);
        this.userApiService.refreshUserNotifications();
        return of(void 0);
      }),
    ).subscribe();

  }

  public acknowledgeAllNotifications(notifications: UserNotification[]) {
    const now = new Date();
    const updatedNotifications: UserNotification[] = notifications
      .filter(n => !n.acknowledgedOn)
      .map(n => {
        const updated: UserNotification = {
          ...n,
          acknowledgedOn: now
        }
        return updated
      });

    if (!updatedNotifications.length) return;

    updatedNotifications.forEach(notification => this.optimisticAcknowledgementById.set(notification.id, notification.acknowledgedOn));
    this.updateDisplayedUserNotifications(updatedNotifications);

    // this.batchAcknowledgeInProgress$.next();
    this.userApiService.setUserNotifications(updatedNotifications).pipe(
      first(),
      tap(() => this.userApiService.refreshUserNotifications()),
      catchError(() => {
        updatedNotifications.forEach(notification => this.optimisticAcknowledgementById.delete(notification.id));
        this.userApiService.refreshUserNotifications();
        return of(void 0);
      }),
    ).subscribe();
  }

  public toggleNotificationSidebar() {
    const next = !this.showNotifications$.value;

    if (next) {
      this.showVacations$.next(false);
    }
    this.showNotifications$.next(next);
  }

  public toggleVacationSidebar() {
    const next = !this.showVacations$.value;

    if (next) {
      this.loadVacationEvents();
      this.showNotifications$.next(false);
    }
    this.showVacations$.next(next);
  }

  @HostListener('window:keydown', ['$event'])
  keyDownEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.showNotifications$.next(false)
      this.showVacations$.next(false)
    }
  }

  @HostListener('window:click', ['$event'])
  @HostListener('window:touchstart', ['$event'])
  mouseClickEvent(event: Event) {
    if (!this.notificationPanel?.nativeElement.contains(event.target)
      && !!this.bellIcon
      && !this.bellIcon.nativeElement.contains(event.target)) {
      this.showNotifications$.next(false)
    }

    if (!this.vacationPanel?.nativeElement.contains(event.target)
      && !!this.vacationIcon
      && !this.vacationIcon.nativeElement.contains(event.target)) {
      this.showVacations$.next(false)
    }
  }

  launchCreateSoftwareRequestDialog() {
    if (this.dialog.displayed$.value) {
      return
    }
    const args: EditSoftwareRequestDialogArgs = { softwareRequest: null };
    const title = 'create software request';


    this.dialog.openDialog(EditSoftwareRequestDialogComponent, title, { args });

    this.dialog.submit$.pipe(
      first(),
      withLatestFrom(
        this.currentUser$,
        this.store.select(CoreSelectors.getUsers)
      ),
      tap(([result, currentUser, users]) => {
        if (result.type === DialogReturnType.SUBMIT) {
          const request = result.value?.softwareRequest;
          if (!request) {
            this.snackbarService.showSnackbar(SnackbarStatus.ERROR, 'Invalid request data');
            return;
          }

          this.store.dispatch(PrismCommonActions.SetSoftwareRequest.initial({ request }));
          listenForResult(
            this.store.select(PrismCommonSelectors.getLoadingState),
            this.store.select(PrismCommonSelectors.getError),
            () => {
              this.snackbarService.showSnackbar(SnackbarStatus.SUCCESS, 'software request created')
              this.dialog.closeDialog();

              const notifications: UserNotification[] = users.map(user => {
                const receivesNotifications: boolean = user.abilities[PrismModule.PrismCommon][PrismCommonSubModule.SoftwareRequests].ReceivesSoftwareRequestNotifications;
                if (!receivesNotifications || currentUser.id == user.id) return null;

                const notification: UserNotification = {
                  id: this.appwrite.createId(),
                  userId: user.id,
                  description: `${currentUser.firstName} requested a new feature: ${request.title}`,
                  createdOn: new Date(),
                  acknowledgedOn: null,
                  associatedUrl: `${PrismModule.PrismCommon}/${PrismCommonSubModule.SoftwareRequests}?requestId=${request.id}`,
                }

                return notification;
              }).filter(isDefinedAssertType);



              this.userApiService.setUserNotifications(notifications);
            },
            (error) => {
              this.snackbarService.showSnackbar(SnackbarStatus.ERROR, error)
            }
          )
        }
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();
  }

  private toDisplayVacationEvents(
    events: VacationEvent[],
    userDict: Record<string, User>,
    employeeDict: Record<string, Employee>,
  ): DisplayVacationEvent[] {
    return [...events]
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
      .map(event => {
        const person = vacationEmployeeDisplay(event, userDict, employeeDict);
        return {
          ...event,
          employeeName: person.name,
          employeeTitle: person.title,
          employeeImageUrl: person.imageUrl,
          employeeInitials: person.initials,
          dateLabel: this.vacationDateLabel(event),
          activeToday: this.vacationIncludesDate(event, new Date()),
        };
      });
  }

  /** Only the org chart people this week's vacations name, not the whole employee table. */
  private loadVacationEmployees(events: VacationEvent[]): Observable<Record<string, Employee>> {
    const ids = [...new Set(events
      .filter(event => event.employeeType === 'employee' && !!event.employeeId)
      .map(event => event.employeeId as string))];
    return this.commonApi.loadEmployeesByIds(ids).pipe(
      catchError(() => of([] as Employee[])),
      map(employees => Object.fromEntries(employees.map(employee => [employee.id, employee]))),
    );
  }

  private loadVacationCount(): void {
    this.commonApi.loadVacationEventCountBetween(
      this.getTodayStart(),
      this.getDaysFromTodayEnd(7)
    ).pipe(
      first(),
      catchError(() => of(0)),
      takeUntil(this.unsubscribe$),
    ).subscribe(count => this.vacationCountSubject$.next(count));
  }

  private loadVacationEvents(): void {
    this.commonApi.loadVacationEventsBetween(
      this.getTodayStart(),
      this.getDaysFromTodayEnd(7)
    ).pipe(
      first(),
      catchError(() => of([])),
      takeUntil(this.unsubscribe$),
    ).subscribe(events => {
      this.vacationEventsSubject$.next(events);
      this.vacationCountSubject$.next(events.length);
    });
  }

  private vacationDateLabel(event: VacationEvent): string {
    const start = this.stripTime(event.startDate);
    const end = this.stripTime(event.endDate);
    const format = (date: Date) => `${this.weekdayLabel(date)} ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

    if (start.getTime() === end.getTime()) {
      return format(start);
    }

    return `${format(start)} - ${format(end)}`;
  }

  private weekdayLabel(date: Date): string {
    return ['Sun.', 'Mon.', 'Tue.', 'Wed.', 'Thur.', 'Fri.', 'Sat.'][date.getDay()];
  }

  private vacationIncludesDate(event: VacationEvent, date: Date): boolean {
    const day = this.stripTime(date).getTime();
    return this.stripTime(event.startDate).getTime() <= day && this.stripTime(event.endDate).getTime() >= day;
  }

  private getTodayStart(): Date {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private getDaysFromTodayEnd(dayCount: number): Date {
    const date = this.getTodayStart();
    date.setDate(date.getDate() + dayCount);
    date.setHours(23, 59, 59, 999);
    return date;
  }

  private stripTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private setDisplayedUserNotifications(userNotifications: UserNotification[]): void {
    // Local optimistic path — keep map entries until a server fetch confirms them.
    const notifications = this.toDisplayedUserNotifications(userNotifications, false);
    this.latestUserNotifications = notifications;
    this.userNotificationsSubject$.next(notifications);
  }

  private setUnacknowledgedNotificationCount(count: number): void {
    this.unacknowledgedNotificationCountSubject$.next(count);
    this.updateFavicon(count);
  }

  /**
   * @param fromServer when true, drop optimistic entries once the server's ack
   *   state matches (so a later poll can't re-introduce a stale unacked row).
   *   Local updates must pass false — otherwise the optimistic map is cleared
   *   immediately on our own write and the next poll races the in-flight save.
   */
  private toDisplayedUserNotifications(userNotifications: UserNotification[], fromServer: boolean): UserNotification[] {
    return this.sortUserNotifications(
      userNotifications.map(notification => this.applyOptimisticAcknowledgement(notification, fromServer))
    );
  }

  private updateDisplayedUserNotifications(updatedNotifications: UserNotification[]): void {
    const updatedNotificationById = new Map(updatedNotifications.map(notification => [notification.id, notification]));
    this.setDisplayedUserNotifications(
      this.latestUserNotifications.map(notification => updatedNotificationById.get(notification.id) || notification)
    );
    this.setUnacknowledgedNotificationCount(
      this.latestUserNotifications.filter(notification => !notification.acknowledgedOn).length
    );
  }

  private applyOptimisticAcknowledgement(notification: UserNotification, fromServer: boolean): UserNotification {
    if (!this.optimisticAcknowledgementById.has(notification.id)) {
      return notification;
    }

    const optimisticAcknowledgedOn = this.optimisticAcknowledgementById.get(notification.id) ?? null;
    // Match on ack state (acked vs not), not exact timestamps — Appwrite may round
    // or rewrite the date, and our local `new Date()` never equals the stored one.
    if (this.notificationAcknowledgementStateMatches(notification.acknowledgedOn, optimisticAcknowledgedOn)) {
      if (fromServer) {
        this.optimisticAcknowledgementById.delete(notification.id);
        return notification; // server is source of truth once confirmed
      }
      // Local path already has the intended value; keep the map entry for later polls.
      return {
        ...notification,
        acknowledgedOn: optimisticAcknowledgedOn,
      };
    }

    // Server (or local base) still disagrees — force the optimistic state so the
    // row doesn't jump back to "unread" while the write is in flight.
    return {
      ...notification,
      acknowledgedOn: optimisticAcknowledgedOn,
    };
  }

  private notificationAcknowledgementStateMatches(current: Date | null, expected: Date | null): boolean {
    return !!current === !!expected;
  }

  private sortUserNotifications(notifications: UserNotification[]): UserNotification[] {
    return [...notifications].sort((a, b) => {
      // Unacknowledged first, then acknowledged.
      if (!!a.acknowledgedOn !== !!b.acknowledgedOn) {
        return a.acknowledgedOn ? 1 : -1;
      }

      // Stable within each group: always sort by createdOn (newest first).
      // Sorting acknowledged rows by acknowledgedOn caused visible reshuffles when
      // server timestamps replaced optimistic ones after rapid multi-ack.
      const aTime = a.createdOn.getTime();
      const bTime = b.createdOn.getTime();
      if (aTime !== bTime) return bTime - aTime;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
  }

  updateFavicon(count: number) {
    const canvas = document.createElement('canvas');
    const size = 32;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    const img = new Image();

    if (count === 0) {
      img.src = '/assets/static/favicon.png'
    } else {
      img.src = '/assets/static/notification_favicon.png'
    }

    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);
      if (count > 0) {
        ctx.fillStyle = '#212121'
        ctx.font = 'bold 18px quicksand'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(count > 99 ? '99+' : count.toString(), size / 2, size / 2)
      }

      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.setAttribute('rel', 'icon');
      link.setAttribute('type', 'image/png');
      link.setAttribute('href', canvas.toDataURL('image/png'));
      document.head.appendChild(link);
    }
  }
}
